import { Link } from 'react-router-dom';
import { Check, ChevronRight, Star } from 'lucide-react';
import type { LocalSignState, Sign } from '@/types/catalog';

interface Props {
  sign: Sign;
  state?: LocalSignState | undefined;
  /** Trecho do nome a destacar (busca). */
  highlight?: string | undefined;
  /** Nome da categoria, quando a lista mistura categorias. */
  categoryName?: string | undefined;
}

/**
 * Destaca o trecho encontrado sem quebrar a acentuação (F9-T05).
 *
 * A busca é feita sobre o texto normalizado, mas o destaque precisa cair sobre o
 * texto original — "AMEM" tem que iluminar "AMÉM". Como a normalização preserva o
 * comprimento (só troca caracteres acentuados por seus equivalentes), os índices
 * do texto normalizado valem no original.
 */
function Highlighted({ text, term }: { text: string; term: string | undefined }): React.ReactNode {
  if (!term) return text;

  const haystack = text.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
  const needle = term.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
  const at = haystack.indexOf(needle);

  if (at < 0 || needle.length === 0 || haystack.length !== text.length) return text;

  return (
    <>
      {text.slice(0, at)}
      <mark className="rounded bg-sky-400/25 text-sky-100">{text.slice(at, at + needle.length)}</mark>
      {text.slice(at + needle.length)}
    </>
  );
}

export function SignListItem({
  sign,
  state,
  highlight,
  categoryName,
}: Props): React.ReactElement {
  const favorite = state?.favorite ?? false;
  const learned = state?.learned ?? false;
  const offline = state?.offline.status === 'available';

  return (
    <li>
      <Link
        to={`/sign/${encodeURIComponent(sign.id)}`}
        className="flex min-h-14 items-center gap-3 rounded-lg px-3 py-2.5 active:bg-slate-800"
      >
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1.5">
            <span className={`truncate ${sign.active ? '' : 'text-slate-500 line-through'}`}>
              <Highlighted text={sign.name} term={highlight} />
            </span>
            {favorite && (
              <Star size={14} className="shrink-0 fill-amber-400 text-amber-400" aria-label="Favorito" />
            )}
            {learned && (
              <Check size={14} className="shrink-0 text-emerald-400" aria-label="Aprendido" />
            )}
          </span>

          {(sign.reference || categoryName) && (
            <span className="mt-0.5 block truncate text-xs text-slate-500">
              {categoryName ? `${categoryName}` : sign.reference}
            </span>
          )}
        </span>

        {offline && (
          <span
            className="size-2 shrink-0 rounded-full bg-emerald-400"
            aria-label="Disponível offline"
            title="Disponível offline"
          />
        )}

        <ChevronRight size={18} className="shrink-0 text-slate-600" aria-hidden />
      </Link>
    </li>
  );
}
